import { Link, NavLink, useNavigate } from "react-router";
import clsx from "clsx";
import {
    ChevronDown,
    LogOut,
    PanelLeftClose,
    PanelLeftOpen,
    Search,
    UserRound,
    X,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useI18n } from "@/hooks/useI18n";
import { NAV_GROUPS } from "@/components/layout/nav";
import { useAuth } from "@/providers/AuthProvider";
import { useDismiss } from "@/hooks/useDismiss";
import { Avatar } from "@/components/ui/Avatar";
import { FlexvLogo } from "@/components/ui/FlexvLogo";

interface SidebarProps {
    onNavigate?: () => void;
    /** Desktop-only rail mode: icons only. */
    collapsed?: boolean;
    onToggleCollapsed?: () => void;
}

export function Sidebar({
    onNavigate,
    collapsed = false,
    onToggleCollapsed,
}: SidebarProps) {
    const { coach, logout } = useAuth();
    const { t } = useI18n();
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const closeMenu = useCallback(() => setMenuOpen(false), []);
    const menuRef = useDismiss(menuOpen, closeMenu);

    // Pencarian tetap menyaring per item, tapi hasilnya dikelompokkan lagi
    // supaya section yang kosong tidak menyisakan judul sendiri.
    const groups = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return NAV_GROUPS;

        return NAV_GROUPS.map((group) => ({
            ...group,
            items: group.items.filter((item) =>
                [t(item.labelKey), t(item.subtitleKey), ...item.keywords]
                    .join(" ")
                    .toLowerCase()
                    .includes(needle),
            ),
        })).filter((group) => group.items.length > 0);
    }, [query, t]);

    const visibleCount = groups.reduce((total, group) => total + group.items.length, 0);

    const displayName = coach?.displayName ?? coach?.coachName ?? "";

    const handleLogout = async () => {
        closeMenu();
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <nav className="flex h-full flex-col">
            {/* Brand: Flexv logo (compact glyph "F" in rail mode) */}
            <div
                className={clsx(
                    "flex h-16 shrink-0 items-center",
                    collapsed ? "justify-center px-0" : "px-5",
                )}
            >
                <FlexvLogo
                    compact={collapsed}
                    title="Flexv"
                    className="app-text-primary h-8 w-auto shrink-0"
                />
            </div>

            {/* Menu search — hidden in rail mode */}
            {!collapsed && (
                <div className="px-3 pb-3">
                    <div className="relative">
                        <Search
                            className="app-text-soft pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                            aria-hidden="true"
                        />
                        <input
                            type="text"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder={t("nav.search")}
                            aria-label={t("nav.search")}
                            className="app-content-subtle app-shell-border app-text-primary w-full rounded-xl border py-2 pl-9 pr-8 text-sm outline-none transition-colors placeholder:text-[var(--app-text-soft)] focus:border-[var(--color-primary)]"
                        />
                        {query && (
                            <button
                                type="button"
                                onClick={() => setQuery("")}
                                aria-label={t("nav.clearSearch")}
                                className="app-text-soft absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 transition-colors hover:text-[var(--app-text-primary)]"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            <ul
                className={clsx(
                    "app-scroll flex-1 overflow-y-auto pb-4",
                    collapsed ? "px-2" : "px-3",
                )}
            >
                {groups.map((group, groupIndex) => (
                    <li key={group.id} className={clsx(groupIndex > 0 && "mt-1")}>
                        {collapsed ? (
                            // Rail mode: cukup pemisah tipis antar section.
                            groupIndex > 0 && (
                                <div className="app-shell-border mx-2 my-2 border-t" />
                            )
                        ) : (
                            <p className="app-text-soft px-2 pb-1.5 pt-3 text-[10px] font-semibold uppercase tracking-[0.16em]">
                                {t(group.labelKey)}
                            </p>
                        )}

                        <ul className="flex flex-col gap-0.5">
                            {group.items.map(({ to, labelKey, icon: Icon }) => (
                                <li key={to}>
                                    <NavLinkItem
                                        to={to}
                                        label={t(labelKey)}
                                        collapsed={collapsed}
                                        onNavigate={onNavigate}
                                    >
                                        <Icon className="h-[18px] w-[18px] shrink-0" />
                                    </NavLinkItem>
                                </li>
                            ))}
                        </ul>
                    </li>
                ))}

                {visibleCount === 0 && (
                    <li className="app-text-soft px-3 py-6 text-center text-xs">
                        {t("nav.noResults")}
                    </li>
                )}
            </ul>

            {/* User profile — pinned to the bottom, no container wrapper */}
            {coach && (
                <div
                    ref={menuRef}
                    className="app-shell-border relative shrink-0 border-t p-3"
                >
                    {menuOpen && (
                        <div
                            role="menu"
                            className="app-shell-panel app-shell-border absolute bottom-full left-3 right-3 mb-2 overflow-hidden rounded-2xl border shadow-xl"
                        >
                            <div className="app-shell-border border-b px-4 py-3">
                                <p className="app-text-primary truncate text-sm font-semibold">
                                    {displayName}
                                </p>
                                <p className="app-text-soft truncate text-xs">
                                    {coach.email ?? coach.username}
                                </p>
                            </div>

                            <Link
                                to="/profile"
                                role="menuitem"
                                onClick={() => {
                                    closeMenu();
                                    onNavigate?.();
                                }}
                                className="app-shell-text-muted app-shell-hover flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
                            >
                                <UserRound className="h-4 w-4" />
                                {t("header.profile")}
                            </Link>

                            <div className="app-shell-border border-t pt-1">
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={handleLogout}
                                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-[var(--color-error-text)] transition-colors hover:bg-[var(--color-error-bg)]"
                                >
                                    <LogOut className="h-4 w-4" />
                                    {t("header.logout")}
                                </button>
                            </div>
                        </div>
                    )}

                    <div
                        className={clsx(
                            "flex items-center gap-2",
                            collapsed && "flex-col",
                        )}
                    >
                        {/* Identity — avatar + name + chevron (opens account menu) */}
                        <button
                            type="button"
                            onClick={() => setMenuOpen((prev) => !prev)}
                            aria-label={t("header.userMenu")}
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                            className={clsx(
                                "flex min-w-0 items-center gap-3 rounded-xl p-1 transition-colors hover:app-content-subtle-hover",
                                collapsed ? "justify-center" : "flex-1 text-left",
                            )}
                        >
                            <Avatar
                                name={displayName}
                                src={coach.avatarUrl}
                                size="md"
                            />
                            {!collapsed && (
                                <>
                                    <span className="flex min-w-0 flex-col">
                                        <span className="app-text-primary truncate text-sm font-semibold leading-tight">
                                            {displayName}
                                        </span>
                                        <span className="app-text-soft truncate text-xs leading-tight">
                                            {coach.email ?? coach.username}
                                        </span>
                                    </span>
                                    <ChevronDown
                                        className={clsx(
                                            "app-text-soft h-4 w-4 shrink-0 transition-transform",
                                            menuOpen && "rotate-180",
                                        )}
                                    />
                                </>
                            )}
                        </button>

                        {/* Minimalist sidebar toggle */}
                        <button
                            type="button"
                            onClick={onToggleCollapsed}
                            aria-label={collapsed ? t("nav.expand") : t("nav.collapse")}
                            title={collapsed ? t("nav.expand") : t("nav.collapse")}
                            className={clsx(
                                "app-shell-text-muted inline-flex shrink-0 items-center justify-center rounded-lg transition-colors hover:app-content-subtle-hover hover:text-[var(--app-text-primary)]",
                                collapsed ? "h-9 w-full" : "h-9 w-9",
                            )}
                        >
                            {collapsed ? (
                                <PanelLeftOpen className="h-[18px] w-[18px]" />
                            ) : (
                                <PanelLeftClose className="h-[18px] w-[18px]" />
                            )}
                        </button>
                    </div>
                </div>
            )}
        </nav>
    );
}

/** Nav link that collapses to an icon-only button in rail mode. */
function NavLinkItem({
    to,
    label,
    collapsed,
    onNavigate,
    children,
}: {
    to: string;
    label: string;
    collapsed: boolean;
    onNavigate?: () => void;
    children: React.ReactNode;
}) {
    return (
        <NavLink
            to={to}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
                clsx(
                    "flex items-center rounded-xl py-2.5 text-sm font-semibold transition-colors",
                    collapsed ? "justify-center px-0" : "gap-3 px-3",
                    isActive
                        ? "app-text-primary"
                        : "app-shell-text-muted hover:app-content-subtle-hover",
                )
            }
            style={({ isActive }) =>
                isActive
                    ? {
                          background: "var(--app-sidebar-active-bg)",
                          color: "var(--app-sidebar-active-text)",
                      }
                    : undefined
            }
        >
            {children}
            {!collapsed && <span className="truncate">{label}</span>}
        </NavLink>
    );
}
